/**
 * AI Skill Form
 *
 * Purpose: Hand-rolled form inputs for creating/editing an AI Skill (name +
 *          markdown content + tool_calls). Stateless — receives formData +
 *          onChange + errors from the parent modal. NOT MetadataForm: skills
 *          are a flat admin entity, not a metadata-driven business model.
 * Architecture: The MD Content field is a split editor/preview — a raw markdown
 *          TextField beside a live-rendered preview pane (react-markdown, the
 *          same renderer used by MarkdownMessage; no new dependency).
 *          The Tools field mirrors the AI Agent Manager's tool_calls Autocomplete
 *          (ORCH-217).
 * Author: Development Team
 * Created: 2026-06-10
 */

import React from 'react';
import { Autocomplete, Box, Chip, Grid, TextField, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { renderMultiSelectOption } from '../common/MultiSelectOption';

export interface AISkillFormData {
  name: string;
  md_content: string;
  tool_calls: string[];
}

interface AISkillFormProps {
  formData: AISkillFormData;
  errors: Record<string, string>;
  canEdit: boolean;
  toolCallOptions: string[];
  onChange: (next: AISkillFormData) => void;
}

const AISkillForm: React.FC<AISkillFormProps> = ({
  formData,
  errors,
  canEdit,
  toolCallOptions,
  onChange,
}) => {
  const set = (patch: Partial<AISkillFormData>) => onChange({ ...formData, ...patch });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
        gap: 2,
        pt: 1,
      }}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Name"
            required
            value={formData.name}
            onChange={(e) => set({ name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name || 'Display name shown in the Agent Manager skills dropdown'}
            fullWidth
            disabled={!canEdit}
            data-testid="ai-skill-name-input"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            renderOption={renderMultiSelectOption((option: string) => option)}
            options={toolCallOptions}
            value={formData.tool_calls}
            onChange={(_e, newValue) => set({ tool_calls: newValue })}
            disabled={!canEdit}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tools"
                error={!!errors.tool_calls}
                helperText={errors.tool_calls || 'Tools this skill is authorized to use'}
                data-testid="ai-skill-tool-calls-input"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  size="small"
                  {...getTagProps({ index })}
                  key={`${option}-${index}`}
                />
              ))
            }
            data-testid="ai-skill-tool-calls-autocomplete"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Skill Content (Markdown)
        </Typography>

        {/* MD Content — split editor / preview that fill the available height */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            flex: 1,
            minHeight: 0,
          }}
        >
          <TextField
            label="MD Content"
            value={formData.md_content}
            onChange={(e) => set({ md_content: e.target.value })}
            error={!!errors.md_content}
            helperText={errors.md_content || 'Prompt / instruction markdown for this skill'}
            fullWidth
            multiline
            minRows={6}
            disabled={!canEdit}
            data-testid="ai-skill-md-content-input"
            sx={{
              flex: 1,
              minHeight: { xs: 200, md: 0 },
              display: 'flex',
              flexDirection: 'column',
              // Make the textarea fill the column height (instead of growing with content).
              '& .MuiInputBase-root': { flex: 1, alignItems: 'stretch', overflow: 'hidden' },
              '& .MuiInputBase-inputMultiline': {
                height: '100% !important',
                overflow: 'auto !important',
                boxSizing: 'border-box',
              },
            }}
          />

          <Box
            data-testid="ai-skill-md-preview"
            sx={{
              flex: 1,
              minHeight: { xs: 200, md: 0 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              overflow: 'auto',
              bgcolor: 'action.hover',
              '& p': { my: 0.5 },
              '& ul, & ol': { my: 0.5, pl: 2.5 },
              '& h1, & h2, & h3': { mt: 1, mb: 0.5 },
              '& code': {
                fontFamily: 'monospace',
                backgroundColor: 'rgba(0,0,0,0.06)',
                px: 0.4,
                py: 0.15,
                borderRadius: 0.5,
              },
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Preview
            </Typography>
            {formData.md_content.trim() ? (
              <ReactMarkdown>{formData.md_content}</ReactMarkdown>
            ) : (
              <Typography variant="body2" color="text.disabled">
                Nothing to preview yet.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AISkillForm;
